import { Box, IconButton, Stack, Typography } from '@mui/material';
import { GetLightInvoicesResponse, InvoiceOverview } from '@vy/api-definition';
import { isNotNullOrUndefined, ItemOf, validateNotNullOrUndefined } from '@vy/utils';
import { VyIcon } from '@vy/ui-design-system';
import { InvoicesActionsMenu } from '../../../../InvoicesPage/invoices-page-components/invoices-actions-menu/InvoicesActionsMenu';
import { SyncInvoicesAsSalesOrderDialog } from '../../../../InvoicesPage/invoices-page-components/SyncInvoicesAsSalesOrderDialog/SyncInvoicesAsSalesOrderDialog';
import { useInvoiceActions } from '../../../../hooks/use-invoice-actions';
import { INVOICE_PDF_TYPES } from '@vy/types';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { useConnectedIntegrations } from '../../../../../../hooks/requests/use-integrations';
import { MarkAsPaidDialog } from '../../../../InvoicesPage/invoices-page-components/MarkAsPaidDialog/MarkAsPaidDialog';
import { useActivate } from '../../../../../../hooks/use-activate';
import { useNavigate } from 'react-router-dom';

type InvoiceActivityHeaderProps = {
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
  handleClose: () => void;
};

export const InvoiceActivityHeader: React.FC<InvoiceActivityHeaderProps> = ({ invoice, handleClose }) => {
  const navigate = useNavigate();
  const [isMarkAsPaidDialogOpen, setMarkAsPaidDialogOpen, setMarkAsPaidDialogClose] = useActivate(false);
  const {
    isInvoiceApplicableForSync,
    downloadInvoicePdf,
    syncToNetSuite,
    syncToNetSuiteSalesOrder,
    confirmSyncToNetSuiteSalesOrder,
    isSalesOrderConfirmDialogOpen,
    handleCloseSalesOrderConfirmDialog,
    syncToStripe,
    approveSelectedInvoice,
    refund,
    grantCredit,
    handleSyncToQuickBooks,
    handleSyncToMorning,
    handleSyncToMarketplace,
    handleSyncToXero,
    handleSyncToPaddle,
  } = useInvoiceActions(invoice as unknown as InvoiceOverview);
  const { data: connectedIntegrations } = useConnectedIntegrations();
  validateNotNullOrUndefined(connectedIntegrations);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 60,
        padding: '16px 16px 16px 24px',
        borderBottom: '1px solid rgba(13, 27, 39, 0.1)',
      }}
    >
      <Typography
        sx={{
          lineHeight: "24px",
          fontSize: 16
        }}>
        Invoice activity
      </Typography>
      <Stack direction="row" spacing={1}>
        {isNotNullOrUndefined(invoice) && (
          <InvoicesActionsMenu
            invoices={[invoice as unknown as InvoiceOverview]}
            onDownloadAsPdf={() => downloadInvoicePdf(INVOICE_PDF_TYPES.Light)}
            onDownloadAsPdfStatement={() => downloadInvoicePdf(INVOICE_PDF_TYPES.Statement)}
            onSyncToNetSuite={syncToNetSuite}
            onSyncToNetSuiteSalesOrder={syncToNetSuiteSalesOrder}
            onSyncToStripe={syncToStripe}
            onSyncToQuickBooks={handleSyncToQuickBooks}
            onSyncToMorning={handleSyncToMorning}
            onSyncToMarketplace={handleSyncToMarketplace}
            onSyncToXero={handleSyncToXero}
            onSyncToPaddle={handleSyncToPaddle}
            onApproveInvoices={approveSelectedInvoice}
            connectedIntegrations={connectedIntegrations}
            syncToNetSuiteDisabled={!isInvoiceApplicableForSync}
            onMarkAsPaid={setMarkAsPaidDialogOpen}
            onRefund={refund}
            contractName={invoice.contract?.contractName ?? ''}
            onSuccessfulRemoval={() => navigate(`/invoices/overview`)}
            onGrantCredits={() =>
              grantCredit({
                invoiceId: invoice.id,
                contractId: invoice.contract?.contractId,
                customerId: invoice.customer.id,
              })
            }
            disableRefund={false}
            disableCreditGrant={false}
          >
            <IconButton {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.dotOptions)}>
              <VyIcon icon="DotsMenu" color="#323232" />
            </IconButton>
          </InvoicesActionsMenu>
        )}
        <IconButton onClick={handleClose} size="small">
          <VyIcon icon="XClose" color="#323232" strokeOpacity={0} />
        </IconButton>
      </Stack>
      {isMarkAsPaidDialogOpen && (
        <MarkAsPaidDialog
          open={isMarkAsPaidDialogOpen}
          invoiceId={invoice.id}
          onClose={() => setMarkAsPaidDialogClose()}
          customerName={invoice.customer.name}
          customerId={invoice.customer.id}
          currency={invoice.currency}
        />
      )}
      <SyncInvoicesAsSalesOrderDialog
        open={isSalesOrderConfirmDialogOpen}
        onClose={handleCloseSalesOrderConfirmDialog}
        invoices={[invoice as unknown as InvoiceOverview]}
        onSyncInvoices={confirmSyncToNetSuiteSalesOrder}
      />
    </Box>
  );
};
